import { useState, useEffect } from 'react'
import { Row, Col, Card, Alert, Spinner } from 'react-bootstrap'
import { api } from '../services/api'

interface SLAPolicy {
  id: string
  name: string
  priority: string
  firstResponseMins: number
  resolutionMins: number
  isActive: boolean
}

export const SLAInfoPage = () => {
  const [policies, setPolicies] = useState<SLAPolicy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchSLAPolicies()
  }, [])

  const fetchSLAPolicies = async () => {
    try {
      setLoading(true)
      // Fetch SLA policies for the user's organization
      const response = await api.get('/tickets/sla-policies')
      setPolicies(response.data.data || [])
    } catch (err: any) {
      setError('Failed to load SLA information')
      console.error('Failed to fetch SLA policies:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60)
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    } else {
      const days = Math.floor(minutes / 1440)
      return `${days} day${days !== 1 ? 's' : ''}`
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'danger'
      case 'high':
        return 'warning'
      case 'medium':
        return 'info'
      case 'low':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="sla-info-page">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-2">
          <i className="fas fa-clock me-2 text-primary"></i>
          Service Level Agreement (SLA) Information
        </h2>
        <p className="text-muted mb-0">
          View our response and resolution time commitments for different priority levels
        </p>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Info Banner */}
      <Card className="mb-4 border-0 bg-light">
        <Card.Body>
          <div className="d-flex align-items-start">
            <i className="fas fa-info-circle text-primary fa-2x me-3"></i>
            <div>
              <h5 className="mb-2">What is an SLA?</h5>
              <p className="mb-0 text-muted">
                A Service Level Agreement (SLA) defines the expected response and resolution times for support tickets 
                based on their priority level. These commitments help ensure you receive timely assistance.
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* SLA Policies */}
      {policies.length > 0 ? (
        <Row>
          {policies.filter(p => p.isActive).map((policy) => (
            <Col md={6} lg={4} key={policy.id} className="mb-4">
              <Card className="border-0 shadow-sm h-100 hover-shadow">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-0">{policy.name}</h5>
                    <span className={`badge bg-${getPriorityColor(policy.priority)} px-3 py-2`}>
                      {policy.priority}
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-reply text-info me-2"></i>
                      <strong>First Response Time:</strong>
                    </div>
                    <div className="ms-4 text-muted">
                      Within {formatTime(policy.firstResponseMins)}
                    </div>
                  </div>

                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      <strong>Resolution Time:</strong>
                    </div>
                    <div className="ms-4 text-muted">
                      Within {formatTime(policy.resolutionMins)}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <i className="fas fa-clock text-muted fa-3x mb-3"></i>
            <h5 className="text-muted">No SLA Policies Available</h5>
            <p className="text-muted mb-0">
              SLA policies have not been configured yet. Please contact your administrator for more information.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Additional Help */}
      <Card className="mt-4 border-0 bg-light">
        <Card.Body>
          <h6 className="mb-3">
            <i className="fas fa-question-circle me-2"></i>
            Need Help Understanding SLAs?
          </h6>
          <ul className="mb-0">
            <li className="mb-2">
              <strong>First Response Time:</strong> The time within which you'll receive an initial response from our support team.
            </li>
            <li className="mb-2">
              <strong>Resolution Time:</strong> The time within which we aim to resolve your issue completely.
            </li>
            <li className="mb-0">
              <strong>Priority Levels:</strong> Higher priority tickets receive faster response and resolution times.
            </li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  )
}

